import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiKept } from "../api/client";
import type { ChapterResult, Discrepancy, Essential, Pickup, Project } from "../api/types";
import { usePickups, useShareLink } from "../lib/pickups";
import { useGeneralNotes } from "../lib/generalNotes";
import { PickupCard, type PickupCardInput } from "../components/PickupCard";
import { CardBoundary } from "../components/CardBoundary";
import { SharingCentre } from "../components/SharingCentre";
import { FrameUploadDialog } from "../components/FrameUploadDialog";
import { GuideTabs } from "../components/GuideTabs";
import { GeneralNotesList } from "../components/GeneralNotes";
import { TimecodeToggle, useTimecodeBasis } from "../components/TimecodeToggle";
import { Icon } from "../components/icons";
import { discrepancyKey, headlineFor, loadState, pendingPickups, type FlagLite } from "../lib/reportStore";
import { fallbackNote, flagSpan, formatTimecode, manuscriptTokensWithSep, settleSpan, type CtxToken, type TimecodeBasis } from "../lib/pickupContext";

// Owner Package card input: live windowed context from the loaded chapter result
// (manuscript tokens + global flag indices → unbounded "show more"), with the
// stored snapshot as the instant fallback. `sepTokens` carries the manuscript's
// original punctuation for this chapter.
// Chapter results loaded together on this page (see resultQueries).
const RESULTS_AT_ONCE = 4;

function ownerInput(
  p: Pickup, result: ChapterResult | undefined, loading: boolean, basis: TimecodeBasis,
  sepTokens: CtxToken[] | undefined, manualIdx: number[] | undefined,
): PickupCardInput {
  const disc = result?.discrepancies.find((d) => discrepancyKey(d) === p.flag_key);
  // A manual flag has no engine discrepancy, but a selection-based one carries
  // its own manuscript anchor (the token indices captured when the reviewer
  // highlighted the passage). Feed those through the same flagSpan path so the
  // Package can window and extend ("… more") the manuscript text just like an
  // engine flag, instead of showing only the fixed snapshot excerpt.
  // Its stored indices point at the chapter's words as they were when it was
  // flagged; settleSpan finds those words again if the chapter was re-proofed.
  const span = disc
    ? flagSpan(disc)
    : manualIdx?.length && sepTokens
    ? settleSpan(sepTokens, manualIdx, { script: p.script_text, before: p.context_before, after: p.context_after })
    : null;
  return {
    flag_key: p.flag_key,
    addressed: p.addressed,
    audio_filename: p.shown_filename || p.audio_filename,
    page: p.page,
    flag_type: p.flag_type || disc?.type || "",
    category: p.category, // reviewer's category override ("" ⇒ card derives from flag_type)
    // The reviewer's edited title/comment live on the pickup snapshot (synced
    // when they're edited in the report). Prefer them over the engine's live
    // values so the Package shows the current/edited versions.
    title: p.title || headlineFor(disc ?? ({ type: p.flag_type ?? "", script_text: p.script_text, heard_text: p.heard_text } as Discrepancy)),
    comment: p.comment || disc?.comment || fallbackNote(p.flag_type || "", p.script_text, p.heard_text, p.start_time, p.end_time),
    timecode: formatTimecode(p.start_time, p.chapter_offset, basis),
    audio_url: p.audio_url ?? result?.audio_url ?? null,
    tokens: span && sepTokens ? sepTokens : null,
    flagFirst: span?.first,
    flagLast: span?.last,
    heardText: disc?.heard_text ?? p.heard_text,
    context_before: p.context_before,
    context_after: p.context_after,
    fallbackFlag: p.flag_type === "added" ? p.heard_text : p.script_text || p.heard_text,
    start_time: p.start_time ?? disc?.start_time ?? null,
    end_time: p.end_time ?? disc?.end_time ?? null,
    // Show the context spinner only while we still expect live context to
    // arrive: an engine flag (its disc appears once the result loads) or a
    // manual flag that has a manuscript anchor. An anchorless manual flag just
    // renders its stored snapshot, so it shouldn't wait on the chapter result.
    contextLoading: loading && !disc && (p.flag_key.startsWith("manual:") ? !!manualIdx?.length : true),
  };
}

// Owner Sharing Centre: enable + copy the public link, or export the package.
function OwnerSharing({ projectId, title, pickupCount, basis, building, essentials }: { projectId: string; title: string; pickupCount: number; basis: TimecodeBasis; building?: boolean; essentials: Essential[] }) {
  const share = useShareLink(projectId);
  const [frame, setFrame] = useState(false);
  const copyUrl = async () => {
    // Copying is an act of sharing: if it's currently off (or no link yet),
    // turn it on first so the copied URL actually works. The token is stable, so
    // re-enabling hands back the same link.
    if (share.enabled && share.url) return share.url;
    const res = await share.enable();
    return `${window.location.origin}/share/${res.token}`;
  };
  return (
    <>
      <SharingCentre
        exportBase={`/api/modules/proofing/projects/${projectId}/pickups/export`}
        copyUrl={copyUrl}
        title={title}
        pickupCount={pickupCount}
        basis={basis}
        shareEnabled={share.enabled}
        onRevoke={share.revoke}
        building={building}
        onFrameUpload={() => setFrame(true)}
      />
      {frame && (
        <FrameUploadDialog projectId={projectId} title={title} essentials={essentials} onClose={() => setFrame(false)} />
      )}
    </>
  );
}

/**
 * Pickups Package — the owner's standalone, chrome-less view of every server-side
 * pickup across a project, numbered, with the audio file, an expandable
 * highlighted snippet, page, category, the report's commentary, a mini player
 * that auditions the displayed fragment, and an "addressed" checkbox. Includes a
 * "Copy share link" control to hand a read-only, no-login version to a
 * narrator/editor.
 */
export function PickupsPackage() {
  const { projectId } = useParams();
  const { data: project } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => api<Project>(`/api/projects/${projectId}`),
  });
  const pickups = usePickups(projectId!, true);
  const notes = useGeneralNotes(projectId!, true);
  const list = pickups.list;
  const addressed = list.filter((p) => p.addressed).length;

  // Heal the Package project-wide: push any flag locally marked "picked" that the
  // server is still missing, so opening the Package (from anywhere) shows every
  // pickup — not only the chapters whose reports happen to have been opened.
  // Idempotent: only pushes what the server lacks.
  const { data: allFlags, isLoading: flagsLoading } = useQuery({
    queryKey: ["flags", projectId],
    queryFn: () =>
      apiKept<{ chapters: Array<{ chapter_id: string; name: string; flags: FlagLite[] }> }>(
        `/api/modules/proofing/projects/${projectId}/flags`
      ),
    enabled: !!projectId,
  });
  useEffect(() => {
    // Wait for the project's files: a manual flag is only pushed for a file of
    // this project (pendingPickups), so an unloaded list would skip them all.
    if (!allFlags || pickups.isLoading || !projectId || !project) return;
    const meta = new Map(
      (project?.chapters ?? []).map((c) => [c.id, { name: c.name, audio_filename: c.audio_filename }])
    );
    for (const snap of pendingPickups(allFlags.chapters, meta, loadState(projectId), pickups.keys)) {
      pickups.pick(snap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFlags, pickups.isLoading, projectId, project]);

  // The package is "building" until the pickup list has loaded AND every flag
  // picked locally has been synced to the server (the heal above). Sharing
  // before that would mint a link missing those pickups — so the Copy-link
  // button stays disabled and reads "Building the package…" until it settles.
  const pendingHeal = useMemo(() => {
    if (!allFlags || pickups.isLoading || !projectId) return 0;
    const meta = new Map(
      (project?.chapters ?? []).map((c) => [c.id, { name: c.name, audio_filename: c.audio_filename }])
    );
    return pendingPickups(allFlags.chapters, meta, loadState(projectId), pickups.keys).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFlags, pickups.isLoading, pickups.keys, projectId, project?.chapters]);
  const building = pickups.isLoading || flagsLoading || pendingHeal > 0;

  // Load each distinct chapter's result once (cached/shared with the report), a few at a time: a long
  // book asked for all ~30 together, each one keeping the server busy for a while, and the server ran out
  // of database connections (Sentry, 22 Oct 2026). The next chapter starts as soon as one finishes.
  const qc = useQueryClient();
  const chapterIds = [...new Set(list.map((p) => p.chapter_id).filter(Boolean))] as string[];
  const settled = chapterIds.filter((cid) => {
    const status = qc.getQueryState(["result", projectId, cid])?.status;
    return status === "success" || status === "error";
  }).length;
  const resultQueries = useQueries({
    queries: chapterIds.map((cid, i) => ({
      queryKey: ["result", projectId, cid],
      queryFn: () =>
        api<ChapterResult>(`/api/modules/proofing/projects/${projectId}/chapters/${cid}/result`),
      staleTime: 60_000,
      enabled: i < settled + RESULTS_AT_ONCE,
    })),
  });
  const resultByChapter: Record<string, ChapterResult | undefined> = {};
  const loadingByChapter: Record<string, boolean> = {};
  chapterIds.forEach((cid, i) => {
    resultByChapter[cid] = resultQueries[i]?.data;
    loadingByChapter[cid] = resultQueries[i]?.isPending ?? false;   // queued or in flight
  });
  // The SECOND phase, and the one you actually see: each chapter's result is
  // fetched to fill the script excerpt into its cards. Purely cosmetic — the
  // export is built server-side from the Pickup rows and never reads any of
  // this — but it's what makes a card look half-finished if you scroll straight
  // down, which is the whole reason this page needs to say it's still working.
  // Counted by what is still IN FLIGHT, not by what has data. A chapter whose
  // result fails to load never gains data, so "missing" as the test left the
  // banner claiming to be working forever — a permanent spinner over a page that
  // had finished, which is worse than saying nothing. A settled query is done,
  // whether it settled into a result or into an error.
  const stillFilling = resultQueries.filter((q) => q.isPending).length;   // queued or in flight
  const filledChapters = chapterIds.length - stillFilling;
  const fillingText = stillFilling > 0;

  // Manuscript tokens with original separators, once per loaded chapter (so the
  // card renders the manuscript's exact punctuation, and "show more" stays cheap).
  const resultsKey = chapterIds.map((cid) => (resultByChapter[cid] ? cid : "")).join(",");
  const sepTokensByChapter = useMemo(() => {
    const out: Record<string, CtxToken[]> = {};
    for (const cid of chapterIds) {
      const r = resultByChapter[cid];
      if (r) out[cid] = manuscriptTokensWithSep(r.manuscript);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsKey]);

  // Manual flags keep their manuscript anchor (token indices) in the review
  // state, keyed `manual:<id>` — the Package uses it to window and extend their
  // text like an engine flag. Present once the reviewer has flagged in this
  // browser; absent elsewhere, where the card falls back to the stored snapshot.
  const manualIdxByKey = useMemo(() => {
    const m = new Map<string, number[]>();
    if (!projectId) return m;
    for (const flags of Object.values(loadState(projectId).manual)) {
      for (const mf of flags) {
        if (mf.manuscript_token_indices?.length) m.set(`manual:${mf.id}`, mf.manuscript_token_indices);
      }
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, list.length]);

  const [basis, setBasis] = useTimecodeBasis();

  return (
    <div className="pickups-standalone">
      <header className="pk-topbar">
        <a className="pk-brand" href="/" target="_blank" rel="noopener noreferrer" title="About Mockingbird">
          <Icon name="logo" size={22} />
          <span className="pk-brand-name">Mockingbird</span>
        </a>
        {projectId && (
          <GuideTabs active="pickups" pickupsTo={`/projects/${projectId}/pickups`} guideTo={`/projects/${projectId}/guide`} />
        )}
      </header>

      <div className="page pickups-page">
        <div className="page-head pk-head">
          <div>
            <div className="pk-eyebrow">Pickups Package</div>
            <h1 className="pk-title">{project?.name ?? "…"}</h1>
            <div className="sub">
              {building && list.length === 0
                ? "Counting…"
                : <>{list.length} pickup{list.length === 1 ? "" : "s"}
                    {list.length > 0 && ` · ${addressed}/${list.length} addressed`}</>}
            </div>
          </div>
          {projectId && (
            <OwnerSharing projectId={projectId} title={project?.name ?? "…"} pickupCount={list.length} basis={basis} building={building} essentials={project?.essentials ?? []} />
          )}
        </div>

        {/* Says which of the two phases is running, because they don't mean the
            same thing: the first decides WHAT is in the package, the second only
            fills in text you can already see the shape of. Sticky, so it stays
            with you if you scroll down into cards that haven't filled yet —
            which is exactly when you'd otherwise think they were wrong. */}
        {(building || fillingText) && (
          <div className="pk-building" role="status" aria-live="polite">
            <span className="spinner" style={{ width: 15, height: 15 }} />
            {building ? (
              <span>Building the package — collecting every flag you've picked up…</span>
            ) : (
              <span className="pk-building-text">
                <span>
                  Filling in the script text — {filledChapters} of {chapterIds.length} file{chapterIds.length === 1 ? "" : "s"} done.
                  <span className="pk-building-sub"> Exports and the share link are already complete.</span>
                </span>
                <span className="progress-track pk-progress" role="progressbar" aria-valuemin={0}
                  aria-valuemax={chapterIds.length} aria-valuenow={filledChapters}>
                  <span className="progress-fill" style={{ display: "block", width: `${(filledChapters / Math.max(1, chapterIds.length)) * 100}%` }} />
                </span>
              </span>
            )}
          </div>
        )}

        {/* Production-wide notes first, so the producer reads them before the
            individual pickups. Shown even when there are no pickups yet. */}
        <GeneralNotesList notes={notes.list} />

        {/* Not while building: an empty list during the load is "we don't know
            yet", and "No pickups yet" states it as a fact. Someone opening the
            page on a slow connection was being told their package was empty. */}
        {list.length === 0 && !building ? (
          <div className="empty-state">
            <h3>No pickups yet</h3>
            <p>Star (★ pick up) flags in the chapter report and they’ll appear here automatically.</p>
          </div>
        ) : (
          <>
            <div className="pk-toolbar">
              <TimecodeToggle value={basis} onChange={setBasis} />
            </div>
            <div className="pickup-list">
              {list.map((p, i) => {
                const input = ownerInput(
                  p,
                  p.chapter_id ? resultByChapter[p.chapter_id] : undefined,
                  p.chapter_id ? loadingByChapter[p.chapter_id] : false,
                  basis,
                  p.chapter_id ? sepTokensByChapter[p.chapter_id] : undefined,
                  manualIdxByKey.get(p.flag_key) ?? p.manuscript_token_indices ?? undefined,
                );
                const onAddressed = (v: boolean) => pickups.setAddressed(p.flag_key, v);
                return (
                  <CardBoundary
                    key={p.flag_key}
                    fallback={<PickupCard index={i} total={list.length} input={{ ...input, tokens: null }} onAddressed={onAddressed} />}
                  >
                    <PickupCard index={i} total={list.length} input={input} onAddressed={onAddressed} />
                  </CardBoundary>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
